import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Wrench, ShoppingBag, Star, CheckCircle2, ShieldCheck, Check } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AuthModal } from '@/components/common'
import type { PartListing } from '@/types/marketplace.types'
import { appRoutes } from '@/constants'
import { useAuth } from '@/providers/auth-provider'
import { useAddToCart } from '@/modules/cart/hooks/use-cart'

interface PartCardProps {
  part: PartListing | any
  layout?: 'grid' | 'list'
  onItemAdded?: () => void
}

export function PartCard({ part, layout = 'grid', onItemAdded }: PartCardProps) {
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [isAdded, setIsAdded] = useState(false)
  const isList = layout === 'list'
  const { isAuthenticated } = useAuth()
  const addToCart = useAddToCart()

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      setAuthModalOpen(true)
      return
    }

    const partNumericId = Number(part.id) || 1
    addToCart.mutate(
      { partId: partNumericId, quantity: 1 },
      {
        onSuccess: () => {
          setIsAdded(true)
          if (onItemAdded) onItemAdded()
          setTimeout(() => setIsAdded(false), 2000)
        },
      },
    )
  }

  // Formatting price
  const formattedPrice = part.priceText || (typeof part.price === 'number' ? `₹${part.price.toLocaleString('en-IN')}` : `₹${part.price}`)

  // Extract primary image
  let primaryImage: string | null = null
  if (Array.isArray(part.images) && part.images.length > 0) {
    const first = part.images[0]
    primaryImage = typeof first === 'string' ? first : first?.url || null
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="h-full"
      >
        <Card
          className={`rounded-2xl border border-border bg-card p-5 flex h-full hover:border-primary/40 transition-all duration-300 group shadow-xs hover:shadow-lg ${
            isList ? 'flex-col sm:flex-row gap-5 items-center' : 'flex-col justify-between'
          }`}
        >
          <div className={`space-y-4 ${isList ? 'flex-1 flex flex-col sm:flex-row gap-5 items-center space-y-0 w-full' : ''}`}>
            {/* Image & OEM Badge */}
            <div
              className={`relative rounded-xl bg-slate-900/10 dark:bg-white/5 border border-border overflow-hidden flex items-center justify-center group-hover:scale-102 transition-transform duration-300 shrink-0 ${
                isList ? 'h-36 w-full sm:w-44' : 'h-44 w-full'
              }`}
            >
              {primaryImage ? (
                <img src={primaryImage} alt={part.title} className="w-full h-full object-cover" />
              ) : (
                <Wrench className="h-12 w-12 text-muted-foreground/30 group-hover:text-primary/50 transition-colors" />
              )}

              <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                <Badge className="bg-background/80 text-[10px] text-foreground border border-border backdrop-blur-md">
                  {part.category}
                </Badge>
                {part.isOEM && (
                  <Badge className="bg-primary/20 text-primary border border-primary/30 backdrop-blur-md">
                    <ShieldCheck className="h-3 w-3 mr-1" /> Genuine OEM
                  </Badge>
                )}
                {Boolean(part.isFeatured || (part.specifications as any)?.isFeatured) && (
                  <Badge className="bg-amber-500 text-black font-extrabold text-[9px] border border-amber-400 backdrop-blur-md flex items-center gap-1 shadow-xs">
                    <Star className="size-2.5 fill-black" /> Featured
                  </Badge>
                )}
                {part.condition && (
                  <Badge
                    className={`text-[9px] backdrop-blur-md font-bold ${
                      part.condition.toUpperCase().includes('USED')
                        ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30'
                        : part.condition.toUpperCase().includes('REFURBISHED')
                        ? 'bg-purple-500/20 text-purple-500 border border-purple-500/30'
                        : 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30'
                    }`}
                  >
                    {part.condition.toUpperCase().includes('USED')
                      ? 'Used / Tested'
                      : part.condition.toUpperCase().includes('REFURBISHED')
                      ? 'Refurbished'
                      : 'New'}
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-2 flex-1 w-full">
              <div className="flex items-center gap-1.5 text-xs text-amber-500 font-medium">
                <Star className="h-3.5 w-3.5 fill-amber-400" />
                <span>{part.ratingText || '5.0 (Verified)'}</span>
              </div>

              <Link to={appRoutes.partDetails(part.slug)}>
                <h3 className="font-display text-base sm:text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                  {part.title}
                </h3>
              </Link>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground font-mono">
                <span>PN: {part.partNumber}</span>
                {(part.vehicleBrand || part.vehicleModel) && (
                  <>
                    <span>•</span>
                    <span className="text-foreground/90 font-semibold font-sans">
                      {part.vehicleBrand} {part.vehicleModel}
                    </span>
                  </>
                )}
              </div>
              {isList && (
                <p className="text-xs text-muted-foreground line-clamp-2 pt-1">{part.description}</p>
              )}
            </div>
          </div>

          <div className={`pt-4 border-t border-border space-y-3 ${isList ? 'sm:border-t-0 sm:border-l sm:pt-0 sm:pl-5 w-full sm:w-48 shrink-0 border-border' : 'mt-4'}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-xl font-extrabold text-foreground">{formattedPrice}</span>
              {part.inStock && (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" /> In Stock
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Link to={appRoutes.partDetails(part.slug)} className="w-full">
                <Button size="sm" variant="outline" className="w-full border-border text-foreground hover:bg-accent font-semibold">
                  Details
                </Button>
              </Link>
              <Button
                size="sm"
                onClick={handleAddToCart}
                disabled={addToCart.isPending}
                className={`w-full font-bold transition-all ${
                  isAdded
                    ? 'bg-emerald-600 text-white'
                    : 'bg-primary text-white hover:bg-primary/90 glow-md'
                }`}
              >
                {isAdded ? (
                  <>
                    <Check className="mr-1 h-3.5 w-3.5" /> Added
                  </>
                ) : (
                  <>
                    <ShoppingBag className="mr-1 h-3.5 w-3.5" /> Add
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>

      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        initialMode="login"
        title="Sign In to Order OEM Parts"
        description="Sign in or register to complete your order and track shipping."
      />
    </>
  )
}
